<template>
  <div class="modal fade show" tabindex="-1" style="display: block; background: rgba(0,0,0,0.5);" v-if="isOpen">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            Gán khách hàng — <span class="text-primary">{{ bankName }}</span>
          </h5>
          <button type="button" class="btn-close" @click="close"></button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <input
              type="text"
              class="form-control"
              placeholder="Tìm kiếm khách hàng..."
              v-model="search"
            />
          </div>

          <div v-if="loadingCustomers" class="text-center py-4">
            <div class="spinner-border text-primary"></div>
          </div>

          <div v-else>
            <div class="d-flex justify-content-between align-items-center mb-2">
              <small class="text-muted">{{ selectedIds.length }} / {{ allCustomers.length }} khách hàng được chọn</small>
              <div>
                <button class="btn btn-link btn-sm p-0 me-3" @click="selectAll">Chọn tất cả</button>
                <button class="btn btn-link btn-sm p-0 text-danger" @click="clearAll">Bỏ chọn</button>
              </div>
            </div>

            <div class="list-group" style="max-height: 400px; overflow-y: auto;">
              <label
                v-for="customer in filteredCustomers"
                :key="customer.id"
                class="list-group-item list-group-item-action d-flex align-items-center gap-3 cursor-pointer"
                :class="{ 'list-group-item-primary': selectedIds.includes(customer.id) }"
              >
                <input
                  class="form-check-input flex-shrink-0"
                  type="checkbox"
                  :value="customer.id"
                  v-model="selectedIds"
                />
                <div>
                  <div class="fw-semibold">{{ customer.name }}</div>
                  <small class="text-muted">{{ customer.email }}</small>
                </div>
              </label>
              <div v-if="filteredCustomers.length === 0" class="list-group-item text-center text-muted py-3">
                Không tìm thấy khách hàng
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="close">Hủy</button>
          <button class="btn btn-primary" @click="save" :disabled="saving">
            <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
            Lưu danh sách
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useBankAccounts } from '@/composables/useBankAccounts'
import { useUser } from '@/composables/useUser'

const emit = defineEmits(['saved'])

const { assignCustomersApi, getAssignedCustomersApi } = useBankAccounts()
const { getUsersApi } = useUser()

const isOpen = ref(false)
const bankAccountId = ref(null)
const bankName = ref('')
const allCustomers = ref([])
const selectedIds = ref([])
const search = ref('')
const loadingCustomers = ref(false)
const saving = ref(false)

const filteredCustomers = computed(() => {
  const q = search.value.trim().toLowerCase()
  if (!q) return allCustomers.value
  return allCustomers.value.filter(c =>
    c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q)
  )
})

const selectAll = () => {
  selectedIds.value = filteredCustomers.value.map(c => c.id)
}

const clearAll = () => {
  selectedIds.value = []
}

const open = async (bank) => {
  bankAccountId.value = bank.id
  bankName.value = bank.bank_name + (bank.account_number ? ` (${bank.account_number})` : '')
  search.value = ''
  selectedIds.value = []
  isOpen.value = true
  loadingCustomers.value = true

  try {
    const [customersRes, assignedRes] = await Promise.all([
      getUsersApi({ limit: 500, search: 'role_id:2', searchFields: 'role_id:=' }),
      getAssignedCustomersApi(bank.id),
    ])

    if (customersRes.success && customersRes.data?.data) {
      allCustomers.value = customersRes.data.data
    }

    if (assignedRes.success) {
      // BE returns { data: [1, 2, 3] } — array of customer IDs
      const ids = assignedRes.data?.data ?? []
      selectedIds.value = Array.isArray(ids) ? ids : []
    }
  } finally {
    loadingCustomers.value = false
  }
}

const close = () => {
  isOpen.value = false
}

const save = async () => {
  saving.value = true
  try {
    const res = await assignCustomersApi(bankAccountId.value, selectedIds.value)
    if (res.success) {
      close()
      emit('saved')
    } else {
      alert(res.error || 'Có lỗi xảy ra')
    }
  } finally {
    saving.value = false
  }
}

defineExpose({ open })
</script>

<style scoped>
.cursor-pointer { cursor: pointer; }
</style>
